// Global error handlers to catch any uncaught errors and prevent white screens

interface ErrorDetails {
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  timestamp: string;
  url: string;
  userAgent: string;
  userId?: string;
}

class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;
  private errorQueue: ErrorDetails[] = [];
  private initialized = false;

  private constructor() {}

  static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  init() {
    if (this.initialized) return;

    // Handle unhandled JavaScript errors
    window.onerror = (message, source, lineno, colno, error) => {
      console.error('Global JavaScript Error:', { message, source, lineno, colno, error });
      
      this.logError({
        message: typeof message === 'string' ? message : 'Unknown error',
        stack: error?.stack,
        filename: source,
        lineno,
        colno,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: localStorage.getItem('userId') || undefined
      });

      // Show user-friendly error instead of letting the page break
      this.showGlobalErrorUI('An unexpected error occurred. Please refresh the page.');
      
      // Prevent the error from breaking the page
      return true;
    };

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);
      
      this.logError({
        message: `Unhandled Promise Rejection: ${event.reason?.message || event.reason}`,
        stack: event.reason?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: localStorage.getItem('userId') || undefined
      });

      // Show user-friendly error
      this.showGlobalErrorUI('A network or processing error occurred. Please try again.');
      
      // Prevent the error from being logged to console again
      event.preventDefault();
    });

    // Handle React errors that somehow escape error boundaries
    window.addEventListener('error', (event) => {
      if (event.error) {
        console.error('Global Error Event:', event.error);
        
        this.logError({
          message: event.error.message || 'Unknown error',
          stack: event.error.stack,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
          userId: localStorage.getItem('userId') || undefined
        });
      }
    });

    this.initialized = true;
    console.log('Global error handlers initialized');
  }

  private logError(error: ErrorDetails) {
    // Add to queue
    this.errorQueue.push(error);
    
    // Keep only last 20 errors
    if (this.errorQueue.length > 20) {
      this.errorQueue.shift();
    }

    // Store in localStorage for debugging
    try {
      localStorage.setItem('global-errors', JSON.stringify(this.errorQueue));
    } catch (e) {
      console.warn('Failed to store errors in localStorage:', e);
    }

    // In production, you would send this to your error tracking service
    if (process.env.NODE_ENV === 'production') {
      this.sendToErrorTrackingService(error);
    }
  }

  private sendToErrorTrackingService(error: ErrorDetails) {
    // Example: Send to Sentry, LogRocket, or your own error tracking
    // try {
    //   fetch('/api/errors', {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify(error)
    //   }).catch(e => console.warn('Failed to send error to service:', e));
    // } catch (e) {
    //   console.warn('Error tracking service failed:', e);
    // }
  }

  private showGlobalErrorUI(message: string) {
    // Create a global error notification that doesn't break the page
    const existingNotification = document.getElementById('global-error-notification');
    if (existingNotification) {
      return; // Don't spam notifications
    }

    const notification = document.createElement('div');
    notification.id = 'global-error-notification';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #fee2e2;
      border: 1px solid #fecaca;
      color: #dc2626;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      max-width: 400px;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      line-height: 1.4;
    `;

    notification.innerHTML = `
      <div style="display: flex; justify-content: between; align-items: start;">
        <div style="flex: 1; margin-right: 12px;">
          <strong>Error Detected</strong><br>
          ${message}
        </div>
        <button onclick="this.parentElement.parentElement.remove()" 
                style="background: none; border: none; font-size: 18px; cursor: pointer; color: #dc2626;">
          ×
        </button>
      </div>
      <div style="margin-top: 12px;">
        <button onclick="window.location.reload()" 
                style="background: #dc2626; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin-right: 8px;">
          Refresh Page
        </button>
        <button onclick="window.location.href = '/'"
                style="background: #6b7280; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
          Go Home
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 10000);
  }

  // Public method to manually trigger error UI (for testing)
  showTestError() {
    this.showGlobalErrorUI('This is a test error notification.');
  }

  // Get stored errors for debugging
  getStoredErrors(): ErrorDetails[] {
    try {
      const stored = localStorage.getItem('global-errors');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.warn('Failed to retrieve stored errors:', e);
      return [];
    }
  }

  // Clear stored errors
  clearStoredErrors() {
    try {
      localStorage.removeItem('global-errors');
      this.errorQueue = [];
    } catch (e) {
      console.warn('Failed to clear stored errors:', e);
    }
  }
}

// Initialize on import
const globalErrorHandler = GlobalErrorHandler.getInstance();

// Export for use in other parts of the app
export default globalErrorHandler;

// Auto-initialize when imported
if (typeof window !== 'undefined') {
  globalErrorHandler.init();
}

// Add to window for debugging in development
if (process.env.NODE_ENV === 'development') {
  (window as any).__globalErrorHandler = globalErrorHandler;
}